import { ReactNode } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { useCustomColors } from "../../hooks/useCustomColors";

export interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  actions?: ReactNode;
  width?: number;
  /** Decorative image parked in the dialog's bottom-right corner. */
  mascot?: string;
}

const AppDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  width = 486,
  mascot,
}: AppDialogProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: `${width}px`,
          maxWidth: "100%",
          m: 2,
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: colors.card.dialog,
          backgroundImage: "none",
          border: `1px solid ${colors.border.accent}`,
          boxShadow: colors.card.dialogShadow,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: colors.tint.scrim } }}
    >
      <Box
        sx={{
          position: "relative",
          px: "28px",
          py: "24px",
          pr: "64px",
          background: colors.gradient.dialogHeader,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t("common_close")}
          sx={{
            position: "absolute",
            top: "12px",
            right: "12px",
            color: "rgba(255, 255, 255, 0.82)",
            "&:hover": { color: colors.accent.gold, backgroundColor: colors.tint.gold },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: "20px" }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "22px",
            fontWeight: 800,
            fontStyle: "italic",
            lineHeight: 1.2,
            color: "#FFFFFF",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              mt: "6px",
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "3px",
              color: colors.accent.gold,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {children && (
        <DialogContent
          sx={{
            px: "28px",
            py: "24px",
            // Room kept clear on the right for the mascot hanging over it.
            pr: mascot ? { xs: "112px", sm: "156px" } : "28px",
            background: colors.card.dialogBody,
            borderTop: `1px solid ${colors.border.accent}`,
          }}
        >
          {children}
        </DialogContent>
      )}

      {actions && (
        <DialogActions
          sx={{
            position: "relative",
            px: "28px",
            py: "20px",
            gap: "12px",
            borderTop: `1px solid ${colors.border.accent}`,
            backgroundColor: colors.card.dialogFooter,
          }}
        >
          {mascot && (
            <Box
              component="img"
              src={mascot}
              alt=""
              aria-hidden="true"
              sx={{
                // Out of flow, so the button below keeps its own geometry: it
                // floats off the action row's top-right rather than sharing it.
                position: "absolute",
                right: "20px",
                bottom: "100%",
                transform: "translateY(35%)",
                width: { xs: "104px", sm: "148px" },
                height: "auto",
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
          )}
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppDialog;
